/**
 * Generic Application Ontology Program — Studio Task 2.
 *
 * The closed, transport-free contracts that thread an
 * `ApplicationOntologyGenerationBinding` through the ORDINARY Studio
 * generation path. Nothing here reads the KG, mints identity, signs, stores an
 * observation, appends a receipt or opens a socket: this module is pure shape
 * and ordering, importable from the browser bundle and from the Pages
 * Functions tree alike.
 *
 * AUTHORITY BOUNDARY (the reason the two shapes below are different types
 * rather than one optional-field type):
 *
 *   - `ApplicationOntologyGenerationBootstrapInput` is what a BROWSER may
 *     say. Five fields, all of them things the user/project already chose:
 *     which profile, which ontology package, which project scope, which actor
 *     scope, which series. It carries no attempt, no KG binding, no receipt,
 *     no chain head, no eligibility, no admission — because Studio does not
 *     own any of those, and a browser that could name them could name a
 *     forged one.
 *   - `StudioApplicationOntologyGenerationContext` is what the SERVER holds
 *     after platform has authenticated the scope, minted the attempt, run the
 *     bounded readback, issued the eligibility and admitted the stage. It is
 *     constructed only by `bootstrapApplicationOntologyGeneration()` (in the
 *     Pages Functions tree) and is never accepted from, or returned to, a
 *     browser caller.
 *
 * Every refusal union is IMPORTED from `@cogo/acceptance-contracts`. This
 * module never declares a refusal code type, never widens one, and never
 * invents a decision branch: the bootstrap decision is `ready` plus the
 * `refused` members of the three canonical decisions, unchanged.
 */
import type { ContentRef } from '@cogo/publisher-core'
import type {
  ApplicationAttemptBinding,
  ApplicationGenerationAdmissionDecision,
  ApplicationGenerationEligibilityIssuanceDecision,
  ApplicationKgPreflightDecision,
  ApplicationSeriesBinding,
  ApplicationStageBinding,
  PlatformApplicationEvidenceAdmissionClient,
} from '@cogo/acceptance-contracts'
import type { ApplicationOntologyGenerationBinding } from '@cogo/schema/application-ontology-generation-binding'
import type { ApplicationProfile } from '@cogo/schema/application-profile'
import type { CompiledApplicationOntology } from '@cogo/schema/compiled-application-ontology'
import type { RequiredCapabilitySlice } from '@cogo/schema/required-capability-slice'
import type { RequiredApplicationTopologySlice } from '@cogo/schema/required-application-topology-slice'

// ─── The five fields a browser may supply ───────────────────────────────

/**
 * The ONLY generation-binding input a public Studio caller may provide.
 *
 * Closed on purpose: `parseApplicationOntologyGenerationBootstrapInput`
 * rejects any object with a sixth key, so `profileSelectionRef`, `attemptId`,
 * `attempt`, `kgPreflight`, `kgPreflightReceiptRef`, `receiptChainHeadRef`,
 * `eligibilityRef`, `binding`, `admissionRef` and a private context are all
 * refused by the same rule rather than by a denylist that a new field name
 * could slip past.
 */
export interface ApplicationOntologyGenerationBootstrapInput {
  profileRef: ContentRef
  applicationOntologyPackageRef: ContentRef
  projectScopeRef: ContentRef
  actorScopeRef: ContentRef
  seriesId: string
}

export const APPLICATION_ONTOLOGY_GENERATION_BOOTSTRAP_INPUT_KEYS = [
  'profileRef',
  'applicationOntologyPackageRef',
  'projectScopeRef',
  'actorScopeRef',
  'seriesId',
] as const satisfies readonly (keyof ApplicationOntologyGenerationBootstrapInput)[]

/**
 * Compile-time proof that the runtime key list above is the WHOLE interface,
 * not a prefix of it. Adding a field to the interface without adding it here
 * makes this line fail to type-check, so the guard can never silently start
 * dropping an authority-bearing field.
 */
type _BootstrapInputKeysAreComplete = Exclude<
  keyof ApplicationOntologyGenerationBootstrapInput,
  (typeof APPLICATION_ONTOLOGY_GENERATION_BOOTSTRAP_INPUT_KEYS)[number]
> extends never
  ? true
  : never
const _bootstrapInputKeysAreComplete: _BootstrapInputKeysAreComplete = true
void _bootstrapInputKeysAreComplete

const CONTENT_HASH = /^sha256:[a-f0-9]{64}$/

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.getPrototypeOf(value) === Object.prototype
  )
}

/** A `ContentRef` is exactly `{ ref, contentHash }` — no extra keys. */
export function isApplicationEvidenceContentRef(
  value: unknown,
): value is ContentRef {
  if (!isPlainRecord(value)) return false
  const keys = Object.keys(value)
  return (
    keys.length === 2 &&
    keys.includes('ref') &&
    keys.includes('contentHash') &&
    typeof value.ref === 'string' &&
    value.ref.trim().length > 0 &&
    typeof value.contentHash === 'string' &&
    CONTENT_HASH.test(value.contentHash)
  )
}

export function sameContentRef(left: ContentRef, right: ContentRef): boolean {
  return left.ref === right.ref && left.contentHash === right.contentHash
}

function cloneRef(value: ContentRef): ContentRef {
  return { ref: value.ref, contentHash: value.contentHash }
}

/**
 * Closed-shape guard for the public bootstrap input.
 *
 * Returns `null` — never a partially-honoured object — for anything that is
 * not exactly the five fields. The returned value is a fresh snapshot, so a
 * caller that mutates its own object after the call cannot change what the
 * server subsequently sends to platform.
 */
export function parseApplicationOntologyGenerationBootstrapInput(
  value: unknown,
): ApplicationOntologyGenerationBootstrapInput | null {
  if (!isPlainRecord(value)) return null
  const keys = Object.keys(value)
  if (keys.length !== APPLICATION_ONTOLOGY_GENERATION_BOOTSTRAP_INPUT_KEYS.length) {
    return null
  }
  for (const key of APPLICATION_ONTOLOGY_GENERATION_BOOTSTRAP_INPUT_KEYS) {
    if (!Object.prototype.hasOwnProperty.call(value, key)) return null
  }
  if (
    !isApplicationEvidenceContentRef(value.profileRef) ||
    !isApplicationEvidenceContentRef(value.applicationOntologyPackageRef) ||
    !isApplicationEvidenceContentRef(value.projectScopeRef) ||
    !isApplicationEvidenceContentRef(value.actorScopeRef) ||
    typeof value.seriesId !== 'string' ||
    value.seriesId.trim().length === 0
  ) {
    return null
  }
  return {
    profileRef: cloneRef(value.profileRef),
    applicationOntologyPackageRef: cloneRef(value.applicationOntologyPackageRef),
    projectScopeRef: cloneRef(value.projectScopeRef),
    actorScopeRef: cloneRef(value.actorScopeRef),
    seriesId: value.seriesId,
  }
}

// ─── The persisted, server-owned selection a Studio surface may READ ────

/**
 * Studio Task 9 — the ONE key under `studio_projects.settings` that says a
 * project's creation surface is ontology-bound.
 *
 * The value is written by the server/project (or by the Library item the user
 * picked), never by a creation surface, and never derived from prompt text, an
 * application name, a display title, a candidate rank or an evidence realm.
 * Its PRESENCE is what makes the surface bound; its `selection` member is the
 * five-field public bootstrap. A bound project whose `selection` is absent or
 * malformed is UNSELECTED, not unbound — the surface pauses.
 */
export const APPLICATION_ONTOLOGY_GENERATION_PROJECT_SETTINGS_KEY =
  'applicationOntologyGeneration'

export type PersistedApplicationOntologyGenerationSelection =
  | { kind: 'unbound' }
  | { kind: 'selection-missing' }
  | { kind: 'selected'; bootstrap: ApplicationOntologyGenerationBootstrapInput }

/**
 * Read the persisted selection off a project-settings row, verbatim.
 *
 * Pure: no KG call, no minting, no ranking, no name lookup, no fallback to a
 * "first candidate". A closed candidate list under this key is a list of
 * CHOICES — with no `selection` beside it the result is `selection-missing`,
 * which the surfaces render as a pause rather than as a generation.
 */
export function readPersistedApplicationOntologyGenerationSelection(
  projectSettings: unknown,
): PersistedApplicationOntologyGenerationSelection {
  if (!isPlainRecord(projectSettings)) return { kind: 'unbound' }
  const bound = projectSettings[APPLICATION_ONTOLOGY_GENERATION_PROJECT_SETTINGS_KEY]
  if (bound === undefined || bound === null) return { kind: 'unbound' }
  if (!isPlainRecord(bound)) return { kind: 'selection-missing' }
  const bootstrap = parseApplicationOntologyGenerationBootstrapInput(
    bound.selection,
  )
  return bootstrap === null
    ? { kind: 'selection-missing' }
    : { kind: 'selected', bootstrap }
}

// ─── Platform-owned wire shapes Studio only ever READS ──────────────────

const APPLICATION_ATTEMPT_KEYS = [
  'attemptId',
  'seriesId',
  'seriesHash',
  'sealedInputHash',
  'profileSelectionRef',
  'projectScopeRef',
  'actorScopeRef',
  'evidenceRealmHash',
  'attemptOrdinal',
  'attemptHash',
  'kgPreflight',
] as const satisfies readonly (keyof ApplicationAttemptBinding)[]

const APPLICATION_KG_PREFLIGHT_BINDING_KEYS = [
  'observationRef',
  'observationHash',
  'producerAttestationRef',
  'snapshotRef',
  'snapshotHash',
  'readbackRef',
  'readbackHash',
  'boundedQueryHash',
] as const satisfies readonly (keyof ApplicationAttemptBinding['kgPreflight'])[]

type _AttemptKeysAreComplete = Exclude<
  keyof ApplicationAttemptBinding,
  (typeof APPLICATION_ATTEMPT_KEYS)[number]
> extends never
  ? true
  : never
const _attemptKeysAreComplete: _AttemptKeysAreComplete = true
void _attemptKeysAreComplete

type _KgBindingKeysAreComplete = Exclude<
  keyof ApplicationAttemptBinding['kgPreflight'],
  (typeof APPLICATION_KG_PREFLIGHT_BINDING_KEYS)[number]
> extends never
  ? true
  : never
const _kgBindingKeysAreComplete: _KgBindingKeysAreComplete = true
void _kgBindingKeysAreComplete

function hasExactKeys(value: unknown, expected: readonly string[]): value is Record<string, unknown> {
  if (!isPlainRecord(value)) return false
  const keys = Object.keys(value)
  return (
    keys.length === expected.length &&
    expected.every((key) => Object.prototype.hasOwnProperty.call(value, key))
  )
}

export { hasExactKeys as hasExactApplicationEvidenceKeys }

/**
 * Structural guard for the attempt platform minted.
 *
 * Studio NEVER constructs one of these — this only decides whether what came
 * back off the wire is the closed shape the contract declares. An INSERTED or
 * REMOVED field inside `kgPreflight` (the classic "extra ref nobody reads")
 * fails here rather than travelling on into the binding.
 */
export function isApplicationAttemptBindingShape(
  value: unknown,
): value is ApplicationAttemptBinding {
  if (!hasExactKeys(value, APPLICATION_ATTEMPT_KEYS)) return false
  const record = value as Record<string, unknown>
  if (
    typeof record.attemptId !== 'string' ||
    record.attemptId.trim().length === 0 ||
    typeof record.seriesId !== 'string' ||
    record.seriesId.trim().length === 0 ||
    typeof record.seriesHash !== 'string' ||
    !CONTENT_HASH.test(record.seriesHash) ||
    typeof record.sealedInputHash !== 'string' ||
    !CONTENT_HASH.test(record.sealedInputHash) ||
    typeof record.evidenceRealmHash !== 'string' ||
    !CONTENT_HASH.test(record.evidenceRealmHash) ||
    typeof record.attemptHash !== 'string' ||
    !CONTENT_HASH.test(record.attemptHash) ||
    !Number.isInteger(record.attemptOrdinal) ||
    (record.attemptOrdinal as number) < 1 ||
    !isApplicationEvidenceContentRef(record.profileSelectionRef) ||
    !isApplicationEvidenceContentRef(record.projectScopeRef) ||
    !isApplicationEvidenceContentRef(record.actorScopeRef)
  ) {
    return false
  }
  const kg = record.kgPreflight
  if (!hasExactKeys(kg, APPLICATION_KG_PREFLIGHT_BINDING_KEYS)) return false
  const kgRecord = kg as Record<string, unknown>
  for (const key of [
    'observationRef',
    'producerAttestationRef',
    'snapshotRef',
    'readbackRef',
  ] as const) {
    if (!isApplicationEvidenceContentRef(kgRecord[key])) return false
  }
  for (const key of [
    'observationHash',
    'snapshotHash',
    'readbackHash',
    'boundedQueryHash',
  ] as const) {
    const hash = kgRecord[key]
    if (typeof hash !== 'string' || !CONTENT_HASH.test(hash)) return false
  }
  return true
}

const APPLICATION_SERIES_BINDING_KEYS = [
  'seriesId',
  'seriesHash',
  'sourceTreeHash',
  'applicationHash',
  'profileHash',
  'applicationOntologyPackageHash',
  'manifestHash',
  'compiledOntologyHash',
  'requiredCapabilitySliceHash',
  'requiredApplicationTopologySliceHash',
  'requiredCapabilityCatalogHash',
  'runtimeConsumerCatalogHash',
  'groundingReportHash',
  'runtimeHash',
  'hostManifestHash',
  'validatorHash',
  'compilerIdentityHash',
  'promptTemplateHash',
  'modelPolicyHash',
  'publicationPolicyHash',
  'gateGraphHash',
] as const satisfies readonly (keyof ApplicationSeriesBinding)[]

type _SeriesKeysAreComplete = Exclude<
  keyof ApplicationSeriesBinding,
  (typeof APPLICATION_SERIES_BINDING_KEYS)[number]
> extends never
  ? true
  : never
const _seriesKeysAreComplete: _SeriesKeysAreComplete = true
void _seriesKeysAreComplete

/**
 * Closed-shape guard for the platform series binding: exactly the contract's
 * 21 fields, `seriesId` non-blank, every hash a `sha256:<64 hex>`.
 *
 * Declared HERE — beside `isApplicationAttemptBindingShape`, importable from
 * the browser bundle and the Pages Functions tree alike — because lane C2b
 * gave it a second consumer: the browser persistence seam closed-parses the
 * `{series, attempt}` the attempt-binding GET route answers (Stage 4), with
 * the SAME guard the Stage-1 persisted-record parser uses server-side.
 */
export function isApplicationSeriesBindingShape(
  value: unknown,
): value is ApplicationSeriesBinding {
  if (!isPlainRecord(value)) return false
  const keys = Object.keys(value)
  if (keys.length !== APPLICATION_SERIES_BINDING_KEYS.length) return false
  for (const key of APPLICATION_SERIES_BINDING_KEYS) {
    if (!Object.prototype.hasOwnProperty.call(value, key)) return false
    const member = value[key]
    if (typeof member !== 'string') return false
    if (key === 'seriesId') {
      if (member.trim().length === 0) return false
    } else if (!CONTENT_HASH.test(member)) {
      return false
    }
  }
  return true
}

// ─── The server-only post-admission context ─────────────────────────────

/**
 * Server-only. Produced ONLY by `bootstrapApplicationOntologyGeneration()`
 * after a canonical `kind: 'admitted'` verification, and never accepted from a
 * browser caller or echoed into a browser-visible response.
 */
export interface StudioApplicationOntologyGenerationContext {
  bootstrap: ApplicationOntologyGenerationBootstrapInput
  profileSelectionRef: ContentRef
  profile: ApplicationProfile
  compiledOntology: CompiledApplicationOntology
  requiredCapabilitySlice: RequiredCapabilitySlice
  requiredApplicationTopologySlice: RequiredApplicationTopologySlice
  kgQueryAuthorityRef: ContentRef
  prerequisiteChainHeadRef: ContentRef
  contractGroundingReportRef: ContentRef
  runtimeRef: ContentRef
  validatorRef: ContentRef
  promptTemplateRef: ContentRef
  sealedInputRef: ContentRef
  modelPolicyRef: ContentRef
  evidenceRealmRef: ContentRef
  requestedExpiresAt: string
  series: ApplicationSeriesBinding
  attempt: ApplicationAttemptBinding
  kgPreflightReceiptRef: ContentRef
  receiptChainHeadRef: ContentRef
  stage: Extract<ApplicationStageBinding, { stage: 'pre-generation' }>
  binding: ApplicationOntologyGenerationBinding
  admissionRef: ContentRef
}

/**
 * `ready` plus the `refused` members of the three canonical platform
 * decisions, verbatim. No Studio-authored refusal branch, no widened code.
 */
export type ApplicationOntologyGenerationBootstrapDecision =
  | { kind: 'ready'; context: StudioApplicationOntologyGenerationContext }
  | Extract<
      | ApplicationKgPreflightDecision
      | ApplicationGenerationEligibilityIssuanceDecision
      | ApplicationGenerationAdmissionDecision,
      { kind: 'refused' }
    >

// ─── The generation-only slice of the platform client ───────────────────

/**
 * Exactly three methods, selected from the canonical platform port.
 *
 * `verifyArtifactPersistenceEligibility` is INTENTIONALLY absent: artifact
 * persistence is Task 4's composition, and a generation-stage component that
 * could call it could admit a persistence stage it has no evidence for.
 */
export type StudioGenerationEvidenceAdmissionClient = Pick<
  PlatformApplicationEvidenceAdmissionClient,
  'runKgPreflight' | 'issueGenerationEligibility' | 'verifyGenerationEligibility'
>

export const STUDIO_GENERATION_EVIDENCE_ADMISSION_CLIENT_KEYS = [
  'runKgPreflight',
  'issueGenerationEligibility',
  'verifyGenerationEligibility',
] as const satisfies readonly (keyof StudioGenerationEvidenceAdmissionClient)[]

type _AdmissionClientKeysAreComplete = Exclude<
  keyof StudioGenerationEvidenceAdmissionClient,
  (typeof STUDIO_GENERATION_EVIDENCE_ADMISSION_CLIENT_KEYS)[number]
> extends never
  ? true
  : never
const _admissionClientKeysAreComplete: _AdmissionClientKeysAreComplete = true
void _admissionClientKeysAreComplete

// ─── The bound order ────────────────────────────────────────────────────

/**
 * The exact, acyclic order a package-BOUND generation must move through.
 *
 * Steps 1-7 happen inside `bootstrapApplicationOntologyGeneration()`; steps 8
 * and 9 happen in the Studio decompose proxy, after — and only after — a
 * canonical admitted verification. Any drift (an authority cross-check that
 * stops mattering, an issuance that overtakes preflight, a prompt built
 * before admission) shows up as a different array, not as a comment that
 * stopped being true.
 */
export const APPLICATION_ONTOLOGY_GENERATION_BOUND_ORDER = [
  'submit-pre-authority-selection-and-scope',
  'platform-run-kg-preflight',
  'receive-platform-ready-attempt-receipt-and-head',
  'cross-check-platform-returned-authority',
  'request-generation-eligibility-issuance',
  'construct-application-ontology-generation-binding',
  'verify-generation-eligibility',
  'construct-decompose-prompt',
  'invoke-model',
] as const

export type ApplicationOntologyGenerationBoundStep =
  (typeof APPLICATION_ONTOLOGY_GENERATION_BOUND_ORDER)[number]

/** Index of the last step the bootstrap itself owns. */
export const APPLICATION_ONTOLOGY_GENERATION_BOOTSTRAP_LAST_STEP: ApplicationOntologyGenerationBoundStep =
  'verify-generation-eligibility'

export interface ApplicationOntologyGenerationBoundStepRecorder {
  record(step: ApplicationOntologyGenerationBoundStep): void
  steps(): readonly ApplicationOntologyGenerationBoundStep[]
}

export function createApplicationOntologyGenerationBoundStepRecorder(): ApplicationOntologyGenerationBoundStepRecorder {
  const steps: ApplicationOntologyGenerationBoundStep[] = []
  return {
    record(step) {
      steps.push(step)
    },
    steps() {
      return [...steps]
    },
  }
}

// ─── The staged planning observation ────────────────────────────────────

/**
 * What the decompose worker hands back for a bound request: the SAME binding
 * it was given (echoed, not re-derived), plus the two refs only the server
 * that actually ran the model can produce.
 *
 * The pre-model binding carries `modelPolicyRef` — the policy the model was
 * allowed to run under. `modelInvocationRef` — the invocation that actually
 * happened — exists only here, because it cannot exist before the call.
 */
export interface ApplicationOntologyGenerationPlanningObservation {
  schemaVersion: 'cogo.application-ontology-generation-planning-observation.v1'
  binding: ApplicationOntologyGenerationBinding
  modelInvocationRef: ContentRef
  decomposeWorkerRef: ContentRef
}

export const APPLICATION_ONTOLOGY_GENERATION_PLANNING_OBSERVATION_SCHEMA_VERSION =
  'cogo.application-ontology-generation-planning-observation.v1'

/**
 * Accepts the observation only when it echoes `expectedBinding` EXACTLY and
 * carries both server-produced refs. A worker that returned a plan without
 * running the model — or that returned a plan for a different binding — fails
 * here, before the plan is promoted.
 */
export function parseApplicationOntologyGenerationPlanningObservation(
  value: unknown,
  expectedBinding: ApplicationOntologyGenerationBinding,
): ApplicationOntologyGenerationPlanningObservation | null {
  if (!isPlainRecord(value)) return null
  const keys = Object.keys(value)
  if (
    keys.length !== 4 ||
    value.schemaVersion !==
      APPLICATION_ONTOLOGY_GENERATION_PLANNING_OBSERVATION_SCHEMA_VERSION ||
    !isApplicationEvidenceContentRef(value.modelInvocationRef) ||
    !isApplicationEvidenceContentRef(value.decomposeWorkerRef) ||
    !isPlainRecord(value.binding)
  ) {
    return null
  }
  if (
    canonicalApplicationEvidenceJson(value.binding) !==
    canonicalApplicationEvidenceJson(expectedBinding)
  ) {
    return null
  }
  return {
    schemaVersion:
      APPLICATION_ONTOLOGY_GENERATION_PLANNING_OBSERVATION_SCHEMA_VERSION,
    binding: expectedBinding,
    modelInvocationRef: cloneRef(value.modelInvocationRef),
    decomposeWorkerRef: cloneRef(value.decomposeWorkerRef),
  }
}

/**
 * The BROWSER's echo check.
 *
 * A browser holds only the five fields it sent, so it verifies exactly those
 * four refs inside the returned binding — plus the presence of both
 * server-produced refs. It is a strict subset of the server-side check above,
 * and deliberately so: a caller cannot verify a field it was never allowed to
 * know, and pretending otherwise would mean shipping the private context to
 * the browser in order to check it.
 */
export function parseApplicationOntologyGenerationPlanningObservationForBootstrap(
  value: unknown,
  bootstrap: ApplicationOntologyGenerationBootstrapInput,
): {
  schemaVersion: typeof APPLICATION_ONTOLOGY_GENERATION_PLANNING_OBSERVATION_SCHEMA_VERSION
  binding: Record<string, unknown>
  modelInvocationRef: ContentRef
  decomposeWorkerRef: ContentRef
} | null {
  if (!isPlainRecord(value)) return null
  const keys = Object.keys(value)
  if (
    keys.length !== 4 ||
    value.schemaVersion !==
      APPLICATION_ONTOLOGY_GENERATION_PLANNING_OBSERVATION_SCHEMA_VERSION ||
    !isApplicationEvidenceContentRef(value.modelInvocationRef) ||
    !isApplicationEvidenceContentRef(value.decomposeWorkerRef) ||
    !isPlainRecord(value.binding)
  ) {
    return null
  }
  const binding = value.binding
  if (
    binding.schemaVersion !== 'cogo.application-ontology-generation-binding.v1'
  ) {
    return null
  }
  const echoed: ReadonlyArray<
    [keyof ApplicationOntologyGenerationBootstrapInput & string, ContentRef]
  > = [
    ['profileRef', bootstrap.profileRef],
    ['applicationOntologyPackageRef', bootstrap.applicationOntologyPackageRef],
    ['projectScopeRef', bootstrap.projectScopeRef],
    ['actorScopeRef', bootstrap.actorScopeRef],
  ]
  for (const [field, expected] of echoed) {
    const actual = binding[field]
    if (!isApplicationEvidenceContentRef(actual) || !sameContentRef(actual, expected)) {
      return null
    }
  }
  return {
    schemaVersion:
      APPLICATION_ONTOLOGY_GENERATION_PLANNING_OBSERVATION_SCHEMA_VERSION,
    binding,
    modelInvocationRef: cloneRef(value.modelInvocationRef),
    decomposeWorkerRef: cloneRef(value.decomposeWorkerRef),
  }
}

function sortedJson(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortedJson)
  if (isPlainRecord(value)) {
    return Object.fromEntries(
      Object.keys(value)
        .sort()
        .map((key) => [key, sortedJson(value[key])]),
    )
  }
  return value
}

/**
 * The ONE definition of "the same binding" in this program.
 *
 * Key-order-insensitive, because a binding that survived a JSON round trip
 * through the decompose transport is the same binding even if its keys come
 * back in another order. Exported so the Studio proxy compares request against
 * response with exactly this function rather than a raw `JSON.stringify` that
 * would disagree with the parser two tiers away about what "same" means.
 */
export function canonicalApplicationEvidenceJson(value: unknown): string {
  return JSON.stringify(sortedJson(value))
}

/**
 * Post-program wiring lane A1, seam 1 — the MCP payload key that carries the
 * admitted binding from `start-app-generation-from-prompt.ts` to the
 * `requestAppGeneration` handler, which forwards it to the durable job create.
 *
 * DECLARED ONCE, USED AS A COMPUTED KEY AT BOTH ENDS. The writer and the
 * reader of an MCP payload key are two files that never see each other, and
 * `payload._somethingElse` is `undefined` rather than an error — so a rename
 * on one side alone would make a bound generation quietly create an UNBOUND
 * durable job. Spelling the name here and indexing with it at both ends turns
 * that rename into a compile error at whichever end did not follow.
 */
export const APP_GENERATION_ONTOLOGY_BINDING_PARAM_KEY =
  '_applicationOntologyGenerationBinding'

// ─── Fail-closed operational refusals (bound requests only) ─────────────

/**
 * The three operational refusals a BOUND request may end in. They are
 * deliberately NOT evidence refusal codes and never become a receipt, a PASS,
 * or an evidence `BLOCKED`: an unreachable planner is an outage, not a
 * verdict about the application.
 */
export const APPLICATION_ONTOLOGY_GENERATION_PLANNER_UNAVAILABLE =
  'application-ontology-generation-planner-unavailable'
export const APPLICATION_ONTOLOGY_GENERATION_FALLBACK_FORBIDDEN =
  'application-ontology-generation-fallback-forbidden'
export const APPLICATION_ONTOLOGY_GENERATION_FAST_PATH_FORBIDDEN =
  'application-ontology-generation-fast-path-forbidden'
/**
 * Lane C2b, Stage 1. A bound attempt that cannot be persisted server-side is
 * an attempt later persistence cannot bind to — proceeding would mint the
 * exact unbound-attempt generation `ATTEMPT_IDENTITY_DRIFT` exists to refuse,
 * so the bound branch fails closed here instead. An operational outage, never
 * an evidence verdict.
 */
export const APPLICATION_ONTOLOGY_GENERATION_BINDING_PERSISTENCE_UNAVAILABLE =
  'application-ontology-generation-binding-persistence-unavailable'

export type ApplicationOntologyGenerationOperationalRefusalCode =
  | typeof APPLICATION_ONTOLOGY_GENERATION_PLANNER_UNAVAILABLE
  | typeof APPLICATION_ONTOLOGY_GENERATION_FALLBACK_FORBIDDEN
  | typeof APPLICATION_ONTOLOGY_GENERATION_FAST_PATH_FORBIDDEN
  | typeof APPLICATION_ONTOLOGY_GENERATION_BINDING_PERSISTENCE_UNAVAILABLE

/**
 * Thrown instead of the ordinary `return null` when the request is bound.
 * Unbound authoring keeps returning `null`, so the difference between the two
 * lanes is the presence of the binding — never the identity of the profile.
 */
export class StudioApplicationOntologyGenerationRefusal extends Error {
  override readonly name = 'StudioApplicationOntologyGenerationRefusal'
  readonly code: ApplicationOntologyGenerationOperationalRefusalCode

  constructor(code: ApplicationOntologyGenerationOperationalRefusalCode) {
    super(code)
    this.code = code
  }
}
